import { cn } from "@/lib/utils";
import type { Orientation, CardLayout } from "@/lib/types";

interface CardMockPreviewProps {
  cardColorHsl: string;
  orientation: Orientation;
  cardLayout?: CardLayout;
  avatarUrl?: string;
  isCutout?: boolean;
  avatarPosition?: { x: number; y: number };
  avatarScale?: number;
  firstName?: string;
  lastName?: string;
  tenantName?: string;
  tenantLogoUrl?: string;
  cardTemplateUrl?: string;
  /** Tenant-managed artwork image rendered behind the avatar (leaves, watermark, pattern). */
  backgroundUrl?: string | null;
  cardNetwork?: "visa" | "mastercard" | "amex" | "discover" | "unionpay";
  showChip?: boolean;
  showCardNetwork?: boolean;
  showSide?: "front" | "back";
  className?: string;
  interactive?: boolean;
  onAvatarDrag?: (pos: { x: number; y: number }) => void;
  onAvatarZoom?: (scale: number) => void;
}

const DEFAULT_LAYOUT: CardLayout = {
  logoPosition: { x: 5, y: 5 },
  chipPosition: { x: 8, y: 45 },
  avatarZone: { mode: "full_surface", shape: "none" },
  namePosition: { x: 5, y: 88 },
  nameSide: "front",
  cardNumberPosition: { x: 5, y: 78 },
  cardNumberSide: "front",
  portraitOverrides: {
    chipPosition: { x: 42, y: 8 },
  },
};

const CardMockPreview = ({
  cardColorHsl,
  orientation,
  cardLayout,
  avatarUrl,
  isCutout = false,
  avatarPosition = { x: 50, y: 50 },
  avatarScale = 1,
  firstName,
  lastName,
  tenantName,
  tenantLogoUrl,
  cardTemplateUrl,
  backgroundUrl,
  cardNetwork = "visa",
  showChip = true,
  showCardNetwork = true,
  showSide = "front",
  className,
  interactive = false,
  onAvatarDrag,
  onAvatarZoom,
}: CardMockPreviewProps) => {
  const isLandscape = orientation === "landscape";
  const layout = cardLayout || DEFAULT_LAYOUT;
  const zone = layout.avatarZone;
  const isFullSurface = zone.mode === "full_surface";

  // Resolve per-orientation positions
  const po = layout.portraitOverrides;
  const logoPos = !isLandscape && po?.logoPosition ? po.logoPosition : layout.logoPosition;
  const chipPosResolved = !isLandscape && po?.chipPosition ? po.chipPosition : layout.chipPosition;
  const namePos = !isLandscape && po?.namePosition ? po.namePosition : layout.namePosition;
  const cardNumPos = !isLandscape && po?.cardNumberPosition ? po.cardNumberPosition : layout.cardNumberPosition;


  const cardStyle = {
    backgroundColor: `hsl(${cardColorHsl})`,
  };

  const avatarObjectPosition = `${avatarPosition.x}% ${avatarPosition.y}%`;
  const avatarTransform = `scale(${avatarScale})`;

  // Clamp avatar drag to the avatar zone boundaries
  const clampToZone = (x: number, y: number) => {
    if (isFullSurface) {
      return {
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y)),
      };
    }
    const r = zone.rect!;
    return {
      x: Math.max(r.x, Math.min(r.x + r.width, x)),
      y: Math.max(r.y, Math.min(r.y + r.height, y)),
    };
  };

  const posFromEvent = (clientX: number, clientY: number, rect: DOMRect) => {
    const rawX = ((clientX - rect.left) / rect.width) * 100;
    const rawY = ((clientY - rect.top) / rect.height) * 100;
    return clampToZone(rawX, rawY);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !onAvatarDrag) return;
    e.preventDefault();
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();

    onAvatarDrag(posFromEvent(e.clientX, e.clientY, rect));

    const onMove = (ev: MouseEvent) => {
      onAvatarDrag(posFromEvent(ev.clientX, ev.clientY, rect));
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!interactive || !onAvatarZoom) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    onAvatarZoom(Math.max(0.3, Math.min(3, avatarScale + delta)));
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!interactive || !onAvatarDrag) return;
    if (e.touches.length === 1) {
      e.preventDefault();
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const touch = e.touches[0];
      onAvatarDrag(posFromEvent(touch.clientX, touch.clientY, rect));
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (e.touches.length === 1 && onAvatarDrag) {
      e.preventDefault();
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const touch = e.touches[0];
      onAvatarDrag(posFromEvent(touch.clientX, touch.clientY, rect));
    }
    if (e.touches.length === 2 && onAvatarZoom) {
      e.preventDefault();
    }
  };

  // Avatar rendering
  const renderAvatar = () => {
    if (!avatarUrl) return null;

    if (isFullSurface) {
      // Full-surface mode: avatar covers the entire card background
      return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <img
            src={avatarUrl}
            alt="Avatar"
            className={cn("h-full w-full", isCutout ? "object-contain" : "object-cover")}
            style={{
              objectPosition: avatarObjectPosition,
              transform: avatarTransform,
              transformOrigin: "center center",
            }}
            draggable={false}
          />
        </div>
      );
    }

    // Restricted mode: avatar constrained to a defined zone
    const r = zone.rect!;
    const shapeClass =
      zone.shape === "circle"
        ? "rounded-full"
        : zone.shape === "rounded_rect"
        ? "rounded-xl"
        : "";

    return (
      <div
        className={cn(
          "absolute overflow-hidden pointer-events-none",
          !isCutout && "border-2 border-white/30 bg-white/10",
          shapeClass
        )}
        style={{
          left: `${r.x}%`,
          top: `${r.y}%`,
          width: `${r.width}%`,
          height: `${r.height}%`,
        }}
      >
        <div className="h-full w-full overflow-hidden">
          <img
            src={avatarUrl}
            alt="Avatar"
            className={cn("h-full w-full", isCutout ? "object-contain" : "object-cover")}
            style={{
              objectPosition: avatarObjectPosition,
              transform: avatarTransform,
              transformOrigin: "center center",
            }}
            draggable={false}
          />
        </div>
      </div>
    );
  };

  // Render the restricted zone outline when interactive (so user knows the boundary)
  const renderZoneGuide = () => {
    if (!interactive || isFullSurface || !zone.rect) return null;
    const r = zone.rect;
    const shapeClass =
      zone.shape === "circle"
        ? "rounded-full"
        : zone.shape === "rounded_rect"
        ? "rounded-xl"
        : "";
    return (
      <div
        className={cn(
          "absolute border border-dashed border-white/30 pointer-events-none",
          shapeClass
        )}
        style={{
          left: `${r.x}%`,
          top: `${r.y}%`,
          width: `${r.width}%`,
          height: `${r.height}%`,
        }}
      />
    );
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl shadow-elevated transition-all select-none",
        isLandscape ? "aspect-[86/54]" : "aspect-[54/86]",
        interactive && "cursor-grab active:cursor-grabbing",
        className
      )}
      style={{ ...cardStyle, ...(interactive ? { touchAction: "none" } : {}) }}
      onMouseDown={handleMouseDown}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      {/* Card template background (full overlay artwork) */}
      {cardTemplateUrl && (
        <img
          src={cardTemplateUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      )}

      {/* Tenant-selected background artwork (sits BEHIND the avatar) */}
      {backgroundUrl && (
        <img
          src={backgroundUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          draggable={false}
        />
      )}

      {/* Avatar layer (behind UI elements) */}
      {renderAvatar()}
      {renderZoneGuide()}

      {/* Logo */}
      <div
        className="absolute flex items-center gap-2 pointer-events-none"
        style={{
          left: `${logoPos.x}%`,
          top: `${logoPos.y}%`,
        }}
      >
        {tenantLogoUrl ? (
          <img src={tenantLogoUrl} alt="" className="h-6 w-auto max-w-[80px] object-contain" draggable={false} />
        ) : (
          <>
            <div className="h-6 w-6 rounded-full bg-primary-foreground/20" />
            <span className="text-xs font-semibold text-white/80 font-display">
              {tenantName || "Bank"}
            </span>
          </>
        )}
      </div>

      {/* Chip */}
      {showChip && (
        <div
          className="absolute rounded-md bg-gradient-to-br from-yellow-300/80 to-yellow-500/80 pointer-events-none"
          style={{
            left: `${chipPosResolved.x}%`,
            top: `${chipPosResolved.y}%`,
            width: isLandscape ? "12%" : "15%",
            height: isLandscape ? "15%" : "12%",
          }}
        />
      )}

      {/* Card number placeholder — only show on matching side */}
      {(layout.cardNumberSide || "front") === showSide && (
        <div
          className="absolute text-xs font-mono tracking-widest text-white/50 pointer-events-none"
          style={{
            left: `${cardNumPos.x}%`,
            top: `${cardNumPos.y}%`,
          }}
        >
          •••• •••• •••• ••••
        </div>
      )}

      {/* Cardholder name — only show on matching side */}
      {(firstName || lastName) && (layout.nameSide || "front") === showSide && (
        <div
          className="absolute text-sm font-semibold tracking-wider text-white/90 uppercase font-display pointer-events-none"
          style={{
            left: `${namePos.x}%`,
            top: `${namePos.y}%`,
          }}
        >
          {firstName} {lastName}
        </div>
      )}

      {/* Card network logo */}
      {showCardNetwork && (
        <div
          className="absolute pointer-events-none"
          style={{
            right: "5%",
            bottom: "5%",
          }}
        >
          <img
            src={`/card-networks/${cardNetwork}.svg`}
            alt={cardNetwork}
            className="h-6 w-auto opacity-90"
            draggable={false}
          />
        </div>
      )}
    </div>
  );
};

export default CardMockPreview;
